import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native-web';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import { colors, radius } from '../theme';
import { getScripts, getPixels, getMappings, createMapping, toggleMapping, deleteMapping } from '../api';

export default function MappingsPage() {
  const [mappings, setMappings] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedScript, setSelectedScript] = useState('');
  const [selectedPixel, setSelectedPixel] = useState('');
  const [priority, setPriority] = useState('0');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [m, s, p] = await Promise.all([getMappings(), getScripts(), getPixels()]);
      setMappings(m);
      setScripts(s);
      setPixels(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!selectedScript || !selectedPixel) return;
    await createMapping({
      scriptId: selectedScript,
      pixelId: selectedPixel,
      priority: parseInt(priority) || 0,
    });
    setModalVisible(false);
    setSelectedScript('');
    setSelectedPixel('');
    setPriority('0');
    load();
  };

  const handleToggle = async (id) => {
    await toggleMapping(id);
    load();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Remove this mapping?')) {
      await deleteMapping(id);
      load();
    }
  };

  // Group mappings by script
  const grouped = mappings.reduce((acc, m) => {
    const key = m.scriptId;
    if (!acc[key]) acc[key] = { scriptName: m.scriptName, scriptId: key, items: [] };
    acc[key].items.push(m);
    return acc;
  }, {});

  return (
    <View>
      <PageHeader title="Mappings" subtitle="Link scripts to backup pixels for Conversions API forwarding">
        <Button label="+ New Mapping" onPress={() => setModalVisible(true)} />
      </PageHeader>

      {Object.values(grouped).map((group) => (
        <Card key={group.scriptId}>
          <Text style={styles.groupTitle}>📜 {group.scriptName}</Text>
          {group.items
            .sort((a, b) => a.priority - b.priority)
            .map((m) => (
              <View key={m.id} style={styles.mappingRow}>
                <View style={styles.arrow}>
                  <Text style={styles.arrowText}>→</Text>
                </View>
                <View style={styles.mappingInfo}>
                  <Text style={styles.pixelName}>🎯 {m.pixelName}</Text>
                  <Text style={styles.pixelIdText}>FB ID: {m.fbPixelId}</Text>
                </View>
                <Badge label={`Priority ${m.priority}`} color="primary" />
                <Badge label={m.active ? 'Active' : 'Off'} color={m.active ? 'success' : 'muted'} />
                <View style={styles.mappingActions}>
                  <Button label={m.active ? 'Disable' : 'Enable'} variant="ghost" size="sm" onPress={() => handleToggle(m.id)} />
                  <Button label="Remove" variant="ghost" size="sm" onPress={() => handleDelete(m.id)} />
                </View>
              </View>
            ))}
        </Card>
      ))}

      {!loading && mappings.length === 0 && (
        <Card>
          <View style={styles.empty}>
            <Text style={styles.emptyIcon}>🔗</Text>
            <Text style={styles.emptyTitle}>No mappings yet</Text>
            <Text style={styles.emptyDesc}>Connect scripts to backup pixels to enable CAPI forwarding</Text>
            <Button label="Create Mapping" onPress={() => setModalVisible(true)} style={{ marginTop: 16 }} />
          </View>
        </Card>
      )}

      <Modal visible={modalVisible} title="New Mapping" onClose={() => setModalVisible(false)}>
        <Text style={styles.selectLabel}>Script</Text>
        <View style={styles.selectWrap}>
          <select
            value={selectedScript}
            onChange={(e) => setSelectedScript(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select a script...</option>
            {scripts.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </View>

        <Text style={styles.selectLabel}>Backup Pixel</Text>
        <View style={styles.selectWrap}>
          <select
            value={selectedPixel}
            onChange={(e) => setSelectedPixel(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select a pixel...</option>
            {pixels.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.pixelId})</option>
            ))}
          </select>
        </View>

        <Text style={styles.selectLabel}>Priority (0 = highest)</Text>
        <View style={styles.selectWrap}>
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            style={{ ...selectStyle, width: 100 }}
          />
        </View>

        <Button label="Create Mapping" onPress={handleCreate} style={{ marginTop: 16 }} />
      </Modal>
    </View>
  );
}

const selectStyle = {
  backgroundColor: '#0f111a',
  color: '#e2e8f0',
  border: '1px solid #2a2e42',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 14,
  width: '100%',
  outline: 'none',
};

const styles = StyleSheet.create({
  groupTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 12,
  },
  mappingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: radius.sm,
    marginBottom: 4,
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  arrow: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(99,102,241,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    color: colors.primary,
    fontWeight: '700',
  },
  mappingInfo: {
    flex: 1,
  },
  pixelName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  pixelIdText: {
    fontSize: 11,
    color: colors.textMuted,
    fontFamily: 'monospace',
  },
  mappingActions: {
    flexDirection: 'row',
    gap: 4,
  },
  selectLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 6,
    marginTop: 12,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  selectWrap: {
    marginBottom: 4,
  },
  empty: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 4,
  },
  emptyDesc: {
    fontSize: 14,
    color: colors.textSecondary,
  },
});
